import { useState, useEffect } from "react";
import useTranslation from "../useTranslation";
import { useCompanies } from "../../context/CompanyContext";
import {
  getSimCards,
  addNewSimCard,
  updateSimCard,
  deleteSimCard,
} from "../../api/simCardApi";
import { toast } from "react-toastify";

export const useSimCardManagement = () => {
  const { t } = useTranslation("productManagerPage");
  const { companies, loading: companiesLoading } = useCompanies();

  // SIM card states
  const [searchTerm, setSearchTerm] = useState("");
  // const [selectedCompany, setSelectedCompany] = useState(""); // Currently not used
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [simCards, setSimCards] = useState([]);
  const [filteredSimCards, setFilteredSimCards] = useState([]);
  const [loadingSimCards, setLoadingSimCards] = useState(false);

  // SIM card modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSimCard, setSelectedSimCard] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [simCardToDelete, setSimCardToDelete] = useState(null);

  // Functions for SIM cards
  const fetchSimCards = async () => {
    try {
      setLoadingSimCards(true);
      // Fetch all SIM cards with no filters from the API
      const data = await getSimCards();
      setSimCards(data);
      setFilteredSimCards(data);
    } catch (error) {
      console.error("Error fetching SIM cards:", error);
      setSimCards([]);
      setFilteredSimCards([]);
    } finally {
      setLoadingSimCards(false);
    }
  };

  // Function to apply filters -- only search term is currently used
  const applyFilters = () => {
    try {
      const newFilteredSimCards = simCards
        .filter((simCard) => {
          if (searchTerm && searchTerm.trim() !== "") {
            const search = searchTerm.toLowerCase().trim();

            // Check across all fields
            const matchesSearch =
              // Check sim
              (simCard.sim && simCard.sim.toLowerCase().includes(search)) ||
              // Check phonenumber
              (simCard.phonenumber &&
                simCard.phonenumber.toLowerCase().includes(search)) ||
              // Check cardname
              (simCard.cardname &&
                simCard.cardname.toLowerCase().includes(search));

            return matchesSearch;
          }
          return true; // If no search term, return all
        })
        .sort((a, b) => a._id - b._id);

      // Only update the filtered view, not the original dataset
      setFilteredSimCards(newFilteredSimCards);
    } catch (error) {
      console.error("Error applying filters:", error);
      setFilteredSimCards([]);
    } finally {
      setLoadingSimCards(false);
    }
  };

  const handleAddSimCard = () => {
    setShowAddModal(true);
    setSelectedSimCard(null);
  };

  const handleSaveSimCard = async (simCardData) => {
    try {
      const response = await addNewSimCard(simCardData);

      if (response.code === 201) {
        toast.success(t("sim_card_added_successfully"));
        setShowAddModal(false);
        fetchSimCards(); // Refresh the list after adding
      } else {
        toast.error(t("sim_card_add_error"));
      }

      return {
        success: true,
      };
    } catch (error) {
      const errorData = error.response?.data;
      const errorCode = errorData?.errorCode || "UNKNOWN_ERROR";

      toast.error(t(errorCode, { value: errorData?.value }));

      return {
        success: false,
        errorCode,
        error: errorData?.message || t("errorAddingSimCard"),
        errorField: errorData?.field,
        errorValue: errorData?.value,
      };
    }
  };

  const handleUpdateSimCard = async (simCardData) => {
    try {
      const response = await updateSimCard(selectedSimCard._id, simCardData);
      if (response.code === 200) {
        toast.success(t("sim_card_updated_successfully"));
        setShowEditModal(false);
        fetchSimCards();
        return { success: true };
      }

      return { success: false };
    } catch (error) {
      console.error("Error updating SIM card:", error);
      const errorData = error.response?.data;
      const errorCode = errorData?.errorCode || "UNKNOWN_ERROR";

      toast.error(t(errorCode, { value: errorData?.value }));
      return {
        success: false,
        errorCode,
        error: errorData?.message || t("sim_card_update_error"),
        errorField: errorData?.field,
        errorValue: errorData?.value,
      };
    }
  };

  // Function to handle deletion of a SIM card
  const handleDeleteSimCard = async (simCardId) => {
    try {
      const response = await deleteSimCard(simCardId);

      if (response.code === 200) {
        return {
          success: true,
          code: response.code,
        };
      }
    } catch (error) {
      console.error("Error deleting SIM card:", error);
      const errorData = error.response?.data;
      const errorCode = errorData?.errorCode || "UNKNOWN_ERROR";

      return {
        success: false,
        errorCode,
        error: errorData?.message || t("errorDeletingSimCard"),
        errorField: errorData?.field,
        errorValue: errorData?.value,
      };
    }
  };

  const handleConfirmDelete = async () => {
    if (simCardToDelete) {
      const response = await handleDeleteSimCard(simCardToDelete._id);

      // If deletion was successful, refresh the list
      if (response && response.code === 200) {
        toast.success(t("sim_card_deleted_successfully"));
        setShowDeleteConfirmation(false);
        fetchSimCards(); // Refresh the list after deletion
      } else {
        toast.error(t("sim_card_delete_error"));
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setSimCardToDelete(null);
  };

  // Handle action clicks
  const handleActionClick = (actionType, simCard) => {
    switch (actionType) {
      case "edit":
        setSelectedSimCard(simCard);
        setShowEditModal(true);
        break;
      case "delete":
        setSimCardToDelete(simCard);
        setShowDeleteConfirmation(true);
        break;
      default:
        break;
    }
  };

  // Effects
  useEffect(() => {
    fetchSimCards();
  }, [currentPage, recordsPerPage]);

  useEffect(() => {
    // Reset to page 1 whenever recordsPerPage changes
    setCurrentPage(1);
  }, [recordsPerPage]);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, simCards]);

  // Calculate pagination
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedSimCards = filteredSimCards.slice(startIndex, endIndex);

  return {
    // State
    searchTerm,
    setSearchTerm,
    /*     selectedCompany,
    setSelectedCompany, */
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    selectedSimCard,
    setSelectedSimCard,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    simCardToDelete,
    setSimCardToDelete,
    simCards,
    filteredSimCards,
    recordsPerPage,
    setRecordsPerPage,
    currentPage,
    setCurrentPage,
    loadingSimCards,
    companies,
    companiesLoading,
    paginatedSimCards,

    // Functions
    handleAddSimCard,
    handleSaveSimCard,
    handleUpdateSimCard,
    handleDeleteSimCard,
    handleConfirmDelete,
    handleCancelDelete,
    handleActionClick,
    applyFilters,
  };
};
